// AiModeSwitch — WHAT the AI makes, chosen in the composer's focus panel
// (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1: "MODES made clear: name +
// one-line description, current one obvious").
//
// The six modes as the kit's segmented control (AppSegmented, the `choice`
// strength — the chosen segment is the ink thumb), each segment the mode's own
// glyph (LINE; Fly's is its pixel mark, pxFly) — the one the composer's mode chip wears, so the panel teaches
// the chip — and beside the track ONE line naming the current mode and what
// it does ("Dashboard — Builds a board of widgets"). Every segment carries its
// name (aria-label) and its own line (the tooltip). The track writes through
// the shell's ONE writer (`setAssistantMode`), exactly like the chip's menu;
// without that writer it renders nothing.
//
// The mode list mirrors AssistantModeChip.scope.modeChipModes (the chip keeps
// its own frank-safe copy; aiModeSwitch.test.js holds the two equal).
export const AiModeSwitch = {
  flow: 'x',
  flexWrap: 'wrap',
  align: 'center start',
  gap: 'Y Z',
  minWidth: '0',
  hide: (el) =>
    !(
      el.context &&
      el.context.functions &&
      typeof el.context.functions.setAssistantMode === 'function'
    ),

  scope: {
    modes() {
      // A comm host (workspace docs/COMM_HOST.md) offers its own modes only —
      // commProfileData.js commAssistantModes(), restated (scope methods are
      // serialized alone).
      let comm = false
      try {
        const host = String((window.location && window.location.hostname) || '').toLowerCase()
        comm =
          /(^|\.)comm?\.symbols\.app$/.test(host) ||
          window.localStorage.getItem('symbols_force_comm') === '1' ||
          (window.location.search || '').includes('comm=1')
      } catch (_) {
        comm = false
      }
      const all = [
        {
          mode: 'assistant',
          key: 'ai.assistantMode.assistant',
          desc: 'ai.assistantMode.desc.assistant',
          glyph: 'lnAssistant'
        },
        {
          mode: 'dashboard',
          key: 'ai.assistantMode.dashboard',
          desc: 'ai.assistantMode.desc.dashboard',
          glyph: 'lnDashboard'
        },
        {
          mode: 'tools',
          key: 'ai.assistantMode.tools',
          desc: 'ai.assistantMode.desc.tools',
          glyph: 'lnTools'
        },
        {
          mode: 'apps',
          key: 'ai.assistantMode.apps',
          desc: 'ai.assistantMode.desc.apps',
          glyph: 'lnApps'
        },
        {
          mode: 'presentation',
          key: 'ai.assistantMode.presentation',
          desc: 'ai.assistantMode.desc.presentation',
          glyph: 'lnPresentation'
        },
        {
          mode: 'fly',
          key: 'ai.assistantMode.fly',
          desc: 'ai.assistantMode.desc.fly',
          glyph: 'pxFly'
        }
      ]
      return comm ? all.filter((x) => ['assistant', 'tools', 'fly'].includes(x.mode)) : all
    },
    current(root) {
      const m = root && root.assistantMode
      return this.modes().some((x) => x.mode === m) ? m : 'assistant'
    },
    line(el, mode) {
      const m = this.modes().find((x) => x.mode === mode) || this.modes()[0]
      return el.call('polyglot', m.key) + ' — ' + el.call('polyglot', m.desc)
    }
  },

  AppSegmented: {
    variant: 'choice',
    compact: true,
    ariaLabel: (el) => el.call('polyglot', 'ai.assistantMode.label'),
    current: (el, s) => el.parent.scope.current(s && s.root),
    select: 'setAssistantMode',
    childExtends: 'AiModeOption',
    childrenAs: 'state',
    children: (el) =>
      el.parent.scope.modes().map((m) => ({
        key: 'mode-' + m.mode,
        value: m.mode,
        text: '',
        glyph: m.glyph,
        name: m.key,
        desc: m.desc
      }))
  },

  // The current mode, named, with what it does — one quiet line that wraps
  // under the track when the panel is narrow (a phone, a long Georgian line).
  ModeLine: {
    tag: 'span',
    minWidth: '0',
    flex: '1 1 12em',
    fontSize: 'Z',
    color: 'caption',
    lineHeight: '1.35',
    overflowWrap: 'anywhere',
    text: (el, s) => el.parent.scope.line(el, el.parent.scope.current(s && s.root))
  }
}

// One mode: the kit segment, its glyph in place of a word.
export const AiModeOption = {
  extends: 'AppSegmentedItem',
  padding: '0 Z',
  ariaLabel: (el, s) => el.call('polyglot', (s && s.name) || ''),
  title: (el, s) =>
    el.call('polyglot', (s && s.name) || '') + ' — ' + el.call('polyglot', (s && s.desc) || ''),
  Icon: {
    name: (el, s) => (s && s.glyph) || 'lnAssistant',
    boxSize: 'iconSm',
    ariaHidden: 'true'
  }
}
